import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { openSignedInTap } from '../utils/openDocument';
import DocumentViewer from './DocumentViewer';
import UploadDocumentModal from './UploadDocumentModal';
import DocumentChangesDialog, { ChangesButton, useChangeSummaries } from './documents/DocumentChanges';

/** A kept filing's row (`filing:<file link id>`). DOCTRACK part 4 asks about these only - a legacy row was never kept. */
const FILING = 'filing:';

/**
 * DOC0 - the documents filed on this property, on the page the property is on.
 *
 * ⭐⭐⭐ WHY THIS EXISTS. Measured live 2026-09-04: 86 documents are filed against properties and
 * NO PAGE IN THE APP SHOWED ONE OF THEM. The only surface in the whole frontend that rendered a
 * filed document was the Deal Room, and `dealRoomService` filters to its own two target types and
 * drops the rest. `api.listDocuments` and `api.getDocument` had ZERO callers. That is this repo's
 * signature defect - built, correct, reachable from nowhere - at its largest scale yet.
 *
 * ⭐⭐ ONE COMPONENT, BOTH PAGES. A deal property (`kind="deal"`) and a Property Database record
 * (`kind="master"`) differ only in which records' filings they resolve, and that difference lives
 * on the server in `propertyDocumentsService`. Two components would be two things to keep in step.
 *
 * ⭐⭐ DOC2 - THE DOOR LIVES HERE, NOT IN A PAGE HEADER. Measured 2026-09-05: the My Deals page
 * had an "Upload Document" button, the Property Database record page had NOTHING, and the
 * "Bulk Upload" beside the database list is a CSV/XLSX property-record importer - so Joseph's
 * *"make it clear if we can add it with that bulk upload button"* has the answer NO. One button
 * in this panel serves both pages, sits where the documents are, and lets the list re-read
 * itself: **the thing that lists documents is the thing that accepts one.**
 *
 * ⛔⛔ EVERY ROW SAYS WHERE IT LIVES. A document filed on the Property Database record and shown
 * unlabelled on a deal page reads as "filed here" - and "filed here" is the fact an LOI would
 * later be built on. His standard, 2026-09-04: magic where it is certain, a visible question
 * where it is not, and never a silent guess. Here the certainty is total (nothing is inferred -
 * `additional_data.master_property_id` is a link the app itself wrote), so the label is not a
 * hedge; it is the difference between showing a document and claiming a filing.
 */

export type PropertyDocumentKind = 'deal' | 'master';

interface PropertyDocument {
  docId: string;
  title: string;
  source: 'filing' | 'legacy';
  filedOn: 'this-record' | 'property-database' | 'my-deals';
  pending: boolean;
  downloadable: boolean;
  category: string | null;
  docDate: string | null;
  sizeBytes: number | null;
  filename: string | null;
  createdAt: string | null;
  /**
   * MOB4: how it opens - the server's answer (`documentOpenMode`), never worked out here from a name.
   * ⚠️ Optional: the frontend deploys apart from the backend, and a list from a server that predates
   * this has no preview route either - so absent means a window, which is what that server can do.
   */
  opensIn?: 'viewer' | 'window';
  /**
   * LOI-PDF1 (the 129th): this document can ALSO be opened as a real PDF made from its Word file - a letter this app
   * filed, on a server that can make one. ⚠️ Optional for the same reason `opensIn` is: an older server says nothing,
   * and nothing means the row is exactly what it was.
   */
  pdf?: boolean;
}

/**
 * ⭐ THE LABEL IS ABSENT FOR THIS RECORD'S OWN DOCUMENTS, ON PURPOSE. Badging every row "filed
 * here" is noise on the common case and trains the eye to skip the badge - which is exactly when
 * the one row that IS from somewhere else stops being noticed.
 */
const FILED_ON_LABEL: Record<PropertyDocument['filedOn'], string | null> = {
  'this-record': null,
  'property-database': 'On the Property Database record',
  'my-deals': 'On a My Deals property',
};

const prettyCategory = (c: string | null): string | null =>
  c && c !== 'other' ? c.replace(/_/g, ' ').replace(/^\w/, (m) => m.toUpperCase()) : null;

const prettySize = (bytes: number | null): string | null => {
  if (!bytes || bytes <= 0) return null;
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

const prettyDate = (iso: string | null): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export default function PropertyDocumentsPanel({
  kind,
  propertyId,
  openAddToken,
  onDocumentAdded,
  onOpenChanges,
}: {
  kind: PropertyDocumentKind;
  propertyId: string;
  /**
   * DOC2c: raise this to open the dialog from somewhere else on the page. A TOKEN, not a boolean -
   * the caller is asking to OPEN and closing is this component's business; a boolean would have to
   * be un-set by the caller and would re-open on every re-render. `0`/undefined never opens.
   */
  openAddToken?: number;
  /** Called after an upload, so a page that shows other data can refresh it too. */
  onDocumentAdded?: () => void;
  /** DOCTRACK part 4: what a row's "N changes" opens. The record page passes its `?doc=` opener; else this panel opens it. */
  onOpenChanges?: (filingId: string) => void;
}) {
  const [docs, setDocs] = useState<PropertyDocument[] | null>(null);
  const [error, setError] = useState('');
  const [opening, setOpening] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [viewing, setViewing] = useState<PropertyDocument | null>(null);
  /** Why the viewer was opened instead of the PDF he asked for - said INSIDE the viewer (the list is behind it). */
  const [viewerNotice, setViewerNotice] = useState<string | null>(null);
  /**
   * ⛔ A FAILED OPEN IS NOT A FAILED LIST. This used to go into `error`, whose branch replaces the
   * whole panel with "Could not load the documents" - one document refusing to open took every
   * other document off the screen.
   */
  const [openError, setOpenError] = useState('');
  const [changesOf, setChangesOf] = useState<string | null>(null);

  /**
   * DOC2c. The property page's header keeps its "Upload Document" button and no longer owns a
   * dialog of its own: it raises this token instead, and the ONE dialog below opens.
   *
   * ⭐⭐⭐ MEASURED, AND THE BACKLOG CALLED IT "cosmetic, deliberate" WHEN IT WAS NEITHER. The page
   * rendered a SECOND `UploadDocumentModal` with independently-written props - `extractOnUpload`
   * and `targetKind` explicit here, left to their defaults there - which agreed only by luck.
   * ⛔⛔ **And the refresh did not agree at all.** The header's `onUploadComplete` reloaded the
   * PAGE's data while this panel loads its documents from a callback keyed on `[kind, propertyId]`,
   * **so a header upload left this list showing the old set until a page reload** - the document
   * filed, and the page not showing it, which is the exact family DOC0 exists to close.
   *
   * ⛔ A token, not a boolean: the page is asking to OPEN, and closing is this component's
   * business. A boolean would need the page to un-set it and would re-open on every re-render.
   * `0` is the initial value and never opens anything.
   */
  useEffect(() => {
    if (openAddToken) setAdding(true);
  }, [openAddToken]);

  const load = useCallback(async () => {
    if (!propertyId) return;
    try {
      const r = await api.listPropertyDocuments(kind, propertyId);
      setDocs((r.documents ?? []) as PropertyDocument[]);
      setError('');
    } catch (e) {
      // ⛔ A FAILURE IS NOT AN EMPTY SHELF. "No documents yet" on a failed read is the app telling
      // him his documents are gone. `docs` stays null so the empty state cannot render.
      setError(e instanceof Error ? e.message : 'Could not load the documents');
    }
  }, [kind, propertyId]);

  useEffect(() => { void load(); }, [load]);

  const changes = useChangeSummaries((docs ?? []).filter((d) => d.docId.startsWith(FILING)).map((d) => d.docId.slice(FILING.length)));
  const openChanges = onOpenChanges ?? setChangesOf;

  /** The document's own signed link - what a window is pointed at, and what the share sheet fetches. */
  const signedUrl = (doc: PropertyDocument) => (): Promise<string> =>
    api.downloadPropertyDocument(kind, propertyId, doc.docId).then((r) => r.url);

  /**
   * MOB4 (the 128th). HIS PHONE REPORT: the LOI of September 30 on 13368 Main Street opened a white
   * screen he could not leave. This signed the link with an API call and THEN opened a window:
   *
   * ⭐ MEASURED IN THE REAL APP as an iPhone home-screen app. At iPhone's default the window never
   * opened at all - the API answered 200 and nothing happened, for EVERY document here. With pop-ups
   * allowed the button sat on "Opening..." under an iOS prompt, then showed a Word file as a white
   * page with a loading bar that never moved.
   *
   * So there are two ways now, and the SERVER says which (`opensIn`):
   *   - `viewer`: the app's own viewer, with its own Close. No window exists to be stranded in.
   *   - `window`: a PDF. The window opens INSIDE the tap and is pointed at the link when it arrives.
   * ⛔ Nothing is awaited before `openSignedInTap` - the first await is what spends the tap.
   */
  const open = (doc: PropertyDocument) => {
    if (doc.opensIn === 'viewer') {
      setViewerNotice(null);
      setViewing(doc);
      return;
    }
    void openInWindow(doc);
  };

  const openInWindow = async (doc: PropertyDocument) => {
    setOpening(doc.docId);
    try {
      await openSignedInTap(signedUrl(doc));
    } catch (e) {
      setOpenError(e instanceof Error ? e.message : 'Could not open the document');
    } finally {
      setOpening(null);
    }
  };

  /**
   * LOI-PDF1 (the 129th). HIS WORDS, with MOB4 working on his phone: "the loi works on phone but how do i make it pdf
   * and see the headers/footers?" The viewer shows a letter's converted BODY - no letterhead, no footer. Where the
   * server says `pdf`, the letter's first button opens the real thing: a PDF made from the Word file itself.
   *
   * ⭐ A PDF, so it opens the way every PDF here does (MOB4): a window opened INSIDE the tap, pointed at the link when
   *    it arrives - on an iPhone that is iOS's own sheet, with every page, a close and a share.
   * ⛔ HE IS NEVER LEFT WITH NOTHING. The first open of a letter MAKES the PDF, and that can be refused (a Word file
   *    edited since, no converter on this server) or fail. `openSignedInTap` has closed its window by then; this says
   *    why beside the list and opens the Word file in the viewer - what "Open" did before there was a PDF at all.
   */
  const openPdf = async (doc: PropertyDocument) => {
    setOpening(doc.docId);
    try {
      await openSignedInTap(() => api.propertyDocumentPdf(kind, propertyId, doc.docId).then((r) => r.url));
    } catch (e) {
      const said = `${e instanceof Error ? e.message : 'The PDF could not be made.'} Showing the Word file instead.`;
      setOpenError(said);
      // ⭐ And INSIDE the viewer (found by looking, the 130th): the list's own message is behind the dialog.
      setViewerNotice(said);
      setViewing(doc);
    } finally {
      setOpening(null);
    }
  };

  const addButton = (
    <button
      type="button"
      onClick={() => setAdding(true)}
      className="ml-auto shrink-0 rounded border border-gray-300 px-2.5 py-1 text-[11.5px] font-semibold text-gray-700 hover:bg-gray-50"
    >
      Add a document
    </button>
  );

  /**
   * ⭐ The dialog is rendered by BOTH branches below, because a read that failed must still let
   * him add one - the door is not part of the failure. `extractOnUpload` is the deal/record
   * asymmetry; see `UploadDocumentModal` for why the database record says no.
   */
  const dialog = (
    <UploadDocumentModal
      isOpen={adding}
      onClose={() => setAdding(false)}
      propertyId={propertyId}
      extractOnUpload={kind === 'deal'}
      targetKind={kind}
      title="Add a document"
      onUploadComplete={() => {
        setAdding(false);
        void load();
        // ⭐ BOTH move. The page's own data (price, size, the AI rail) can change on an
        // extraction, and this list is not the page's to reload.
        onDocumentAdded?.();
      }}
    />
  );

  if (error) {
    return (
      <div className="mt-[18px] border-t border-gray-150 pt-[18px]">
        <div className="mb-1 flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
          <div className="text-[9.5px] font-bold uppercase leading-none tracking-[.14em] text-primary-500">
            Documents
          </div>
          {addButton}
        </div>
        <p className="mt-2.5 text-[12.5px] text-gray-500">Could not load the documents - {error}</p>
        {dialog}
      </div>
    );
  }

  /**
   * DOC0c IS REVERSED (2026-09-08, Joseph's call). This panel lists EVERY document, always.
   *
   * ⭐⭐⭐ HE ASKED "what happened to the documents? i dont see all of them anymore" - and the fold
   * was the answer. Five rows showed, "Show all 7 documents" held the rest, and the effect on the
   * person the feature exists for was that his documents had gone.
   *
   * ⛔⛔ THE MEASUREMENT THAT JUSTIFIED THE FOLD ALSO ARGUED AGAINST IT, AND I SHIPPED IT ANYWAY.
   * The note right here used to read: "this panel is 4.5% of a 17,730px page whose AI Valuation
   * section alone is 8,710px (49%)". So the fold hid the documents to save ~216px on a page whose
   * real weight is somewhere else entirely - **DOC0's own price paid for 1.2% of the page.**
   *
   * ⭐⭐ HIS STANDARD IS THE RULE HERE: *"if the document should be there, I'd like it to be
   * there."* A count plus a control is not the same as the document being on screen. The height
   * belongs to the sections that actually own it, and those are being dealt with separately.
   */

  if (!docs) return null;

  return (
    <div className="mt-[18px] border-t border-gray-150 pt-[18px]">
      <div className="mb-1 flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
        <div className="text-[9.5px] font-bold uppercase leading-none tracking-[.14em] text-primary-500">
          Documents
        </div>
        {/* ⭐ A COUNT BESIDE A LIST IS A PROMISE ABOUT THE LIST. This is `docs.length` and the
            rows below are `docs.map`, so the two cannot drift apart. */}
        {docs.length > 0 && (
          <span className="text-[11.5px] text-gray-400">
            {docs.length} on file
          </span>
        )}
        {addButton}
      </div>

      {docs.length === 0 && (
        <p className="mt-2.5 text-[12.5px] text-gray-500">
          No documents yet - anything uploaded to this property shows up here.
        </p>
      )}

      {openError && (
        <p role="alert" className="mt-2.5 text-[12.5px] text-red-600">
          Could not open that document - {openError}
        </p>
      )}

      {changes.failed && (
        <p className="mt-2.5 text-[12.5px] text-gray-500">Could not check these documents for changes.</p>
      )}

      {docs.length > 0 && (
        <ul className="mt-2 divide-y divide-gray-150">
          {docs.map((d) => {
            const label = FILED_ON_LABEL[d.filedOn];
            const meta = [prettyCategory(d.category), prettySize(d.sizeBytes), prettyDate(d.docDate ?? d.createdAt)]
              .filter(Boolean) as string[];
            const summary = d.docId.startsWith(FILING) ? changes.byLink[d.docId.slice(FILING.length)] : undefined;
            return (
              <li
                key={d.docId}
                data-doc-row
                className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1 py-2.5"
              >
                <span className="min-w-0 flex-1 basis-[240px] break-words text-[13px] font-medium text-gray-800">
                  {d.title}
                </span>

                {d.pending && (
                  <span className="rounded border border-amber-300 bg-amber-50 px-2 py-0.5 text-[11px] text-amber-800">
                    Awaited - not provided yet
                  </span>
                )}

                {label && (
                  <span className="rounded border border-gray-300 bg-gray-50 px-2 py-0.5 text-[11px] text-gray-600">
                    {label}
                  </span>
                )}

                {meta.length > 0 && (
                  <span className="text-[11.5px] text-gray-400">{meta.join(' · ')}</span>
                )}

                {summary && <ChangesButton summary={summary} onOpen={openChanges} />}

                {/* A slot has nothing to open, so it offers nothing - never a link to nothing. */}
                {d.downloadable && !d.pdf && (
                  <button
                    type="button"
                    onClick={() => { setOpenError(''); open(d); }}
                    disabled={opening === d.docId}
                    className="ml-auto shrink-0 rounded border border-gray-300 px-2.5 py-1 text-[11.5px] font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    {opening === d.docId ? 'Opening...' : 'Open'}
                  </button>
                )}

                {/* LOI-PDF1: a filed letter has TWO things to open, and each button names its own (a control names
                    its contents): the PDF - the letter as it prints, header and footer - and the Word file. */}
                {d.downloadable && d.pdf && (
                  <span className="ml-auto flex shrink-0 gap-1.5">
                    <button
                      type="button"
                      onClick={() => { setOpenError(''); void openPdf(d); }}
                      disabled={opening === d.docId}
                      className="rounded border border-gray-300 px-2.5 py-1 text-[11.5px] font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      {opening === d.docId ? 'Opening PDF...' : 'Open PDF'}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setOpenError(''); setViewerNotice(null); setViewing(d); }}
                      className="rounded border border-gray-300 px-2.5 py-1 text-[11.5px] font-semibold text-gray-700 hover:bg-gray-50"
                    >
                      Word file
                    </button>
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {dialog}

      {changesOf && <DocumentChangesDialog filingId={changesOf} onClose={() => setChangesOf(null)} />}

      {viewing && (
        <DocumentViewer
          property={{ kind, id: propertyId }}
          docId={viewing.docId}
          title={viewing.title}
          notice={viewerNotice}
          onClose={() => { setViewing(null); setViewerNotice(null); }}
          onDownload={() => {
            setOpenError('');
            void openSignedInTap(signedUrl(viewing)).catch((e: unknown) =>
              setOpenError(e instanceof Error ? e.message : 'Could not open the document'));
          }}
          fileUrl={signedUrl(viewing)}
        />
      )}
    </div>
  );
}
